import type { RynReplayEventProperties } from '@ups/xplat/base/ryn-replay';
import { IRynReplayGridColumnDef } from './ryn-replay-grid-columns';

/**
 * Default cap so a misconfigured page size cannot shove a megabyte JSON blob
 * into the replay stream. Pages override via `maxRows` / `take`.
 */
export const RYN_REPLAY_GRID_SNAPSHOT_DEFAULT_MAX_ROWS = 200;

/**
 * Stored in place of every cell value whose column is not in `unmaskedColumns`.
 * Grid snapshots bypass rrweb's input masking, so cells are masked by default.
 */
export const RYN_REPLAY_GRID_MASKED_VALUE = '***';

/** One slim cell value for `rowsJson` (track() is string|number only at the bag root). */
export type RynReplayGridSnapshotCell = string | number | boolean;

/**
 * Page-specific scalar props (job id, dates, …). Values are coerced; nullish
 * become `''` so callers do not have to special-case optionals.
 */
export type RynReplayGridSnapshotMeta = Record<
  string,
  string | number | null | undefined
>;

export interface IRynReplayGridSnapshotBuildInput<TRow> {
  /** Why this snapshot was taken (`load` / `save` / page-specific). */
  reason: string;
  /**
   * Scalar context shown in the player header. Keys are free-form so a new
   * blocked grid does not require a shared DTO change.
   */
  meta?: RynReplayGridSnapshotMeta;
  rows: TRow[] | null | undefined;
  /**
   * Column set to record and display. Prefer the grid's *visible* columns at
   * capture time so user column chooser / defaults are what support sees —
   * not a hardcoded player schema.
   */
  columns: readonly IRynReplayGridColumnDef[];
  /** Map one domain row to a slim JSON-safe object (keys should match columns). */
  slimRow: (row: TRow) => Record<string, RynReplayGridSnapshotCell>;
  /**
   * Column keys whose values may be stored as-is. Every other non-empty cell is
   * replaced with {@link RYN_REPLAY_GRID_MASKED_VALUE}. Default: none, so all
   * cell values are masked unless a page deliberately opts a column in.
   */
  unmaskedColumns?: readonly string[];
  /** Drop aggregate / non-data rows. Default: keep all. */
  includeRow?: (row: TRow) => boolean;
  /** Absolute ceiling (defaults to {@link RYN_REPLAY_GRID_SNAPSHOT_DEFAULT_MAX_ROWS}). */
  maxRows?: number;
  /** Visible page size; used with maxRows to choose the slice. */
  take?: number | null;
  skip?: number | null;
  /** Page id from the app provider (stamped as pageId). */
  pageId?: string;
  /** Player title (stamped for schema-free playback). */
  title?: string;
  /**
   * Route needles for hide-on-leave when the player has no compiled schema
   * (stamped as comma-joined `urlIncludes`).
   */
  urlIncludes?: string | readonly string[];
  /**
   * Extra bag entries merged last (still string|number only). Prefer `meta`
   * for displayable scalars; use this for rare capture-only flags.
   */
  extras?: RynReplayEventProperties;
}

const asProp = (value: string | number | null | undefined): string | number => {
  if (value == null) {
    return '';
  }
  return value;
};

const truncateMetaString = (value: string, maxLen: number): string =>
  value.length > maxLen
    ? `${value.slice(0, Math.max(0, maxLen - 3))}...`
    : value;

const maskRow = (
  row: Record<string, RynReplayGridSnapshotCell>,
  unmasked: ReadonlySet<string>
): Record<string, RynReplayGridSnapshotCell> =>
  Object.fromEntries(
    Object.entries(row).map(([key, value]) => [
      key,
      unmasked.has(key) || value === '' ? value : RYN_REPLAY_GRID_MASKED_VALUE,
    ])
  );

const normalizeColumns = (
  columns: readonly IRynReplayGridColumnDef[]
): IRynReplayGridColumnDef[] =>
  (columns ?? [])
    .filter((col) => !!col?.key)
    .map((col) => ({
      key: col.key,
      label: col.label || col.key,
      ...(col.align ? { align: col.align } : {}),
      ...(col.format ? { format: col.format } : {}),
    }));

/**
 * Build a Ryn Replay `track()` property bag for a blocked Kendo (or similar)
 * grid (#31398). rrweb leaves the DOM alone; this JSON snapshot is what the
 * player table renders.
 *
 * `rowsJson` / `columnsJson` are strings because {@link RynReplayEventProperties}
 * is string|number only.
 */
export function buildRynReplayGridSnapshot<TRow>(
  input: IRynReplayGridSnapshotBuildInput<TRow>
): RynReplayEventProperties {
  const absoluteMax =
    input.maxRows && input.maxRows > 0
      ? input.maxRows
      : RYN_REPLAY_GRID_SNAPSHOT_DEFAULT_MAX_ROWS;
  const take = input.take && input.take > 0 ? input.take : 25;
  const maxRows = Math.min(take, absoluteMax);
  const source = Array.isArray(input.rows) ? input.rows : [];
  const include = input.includeRow ?? (() => true);
  const filtered = source.filter((row) => include(row));
  const unmasked = new Set(input.unmaskedColumns ?? []);
  const slim = filtered
    .slice(0, maxRows)
    .map((row) => maskRow(input.slimRow(row), unmasked));
  const columns = normalizeColumns(input.columns);

  const bag: RynReplayEventProperties = {
    reason: input.reason,
    skip: input.skip ?? 0,
    take: maxRows,
    rowCount: slim.length,
    truncated: filtered.length > maxRows ? 1 : 0,
    columnCount: columns.length,
    columnsJson: JSON.stringify(columns),
    rowsJson: JSON.stringify(slim),
  };

  if (input.pageId) {
    bag['pageId'] = input.pageId;
  }
  if (input.title) {
    bag['title'] = input.title;
  }
  if (input.urlIncludes) {
    const list = Array.isArray(input.urlIncludes)
      ? input.urlIncludes
      : [input.urlIncludes];
    bag['urlIncludes'] = list.filter((s) => !!s).join(',');
  }

  if (input.meta) {
    for (const [key, raw] of Object.entries(input.meta)) {
      const value = asProp(raw);
      bag[key] =
        typeof value === 'string' && value.length > 120
          ? truncateMetaString(value, 120)
          : value;
    }
  }

  if (input.extras) {
    Object.assign(bag, input.extras);
  }

  return bag;
}

/**
 * Leave marker for a blocked-grid page. Player hides the snapshot table from
 * this timestamp until a later `:snapshot` for the same page.
 */
export function buildRynReplayGridLeaveProperties(
  extras?: RynReplayEventProperties
): RynReplayEventProperties {
  return {
    reason: 'leave',
    ...(extras ?? {}),
  };
}
